// src/components/themes/default/admin/BranchInventoryTab.tsx
"use client";

import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import {
  Package,
  Search,
  RefreshCw,
  AlertTriangle,
  ArrowUpDown,
  Plus,
  Minus,
  CheckCircle2,
  Barcode,
  Layers,
  History,
  TrendingDown,
  Warehouse,
} from "lucide-react";

interface BranchInventoryTabProps {
  branchInfo: any;
  subdomain: string;
}

export default function BranchInventoryTab({
  branchInfo,
  subdomain,
}: BranchInventoryTabProps) {
  const [inventories, setInventories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<"all" | "low_stock">("all");
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [adjustmentQty, setAdjustmentQty] = useState<number>(1);
  const [adjustmentType, setAdjustmentType] = useState<"add" | "remove">("add");
  const [actionLoading, setActionLoading] = useState(false);

  const getApiUrl = (endpoint: string) => {
    const isLocal =
      typeof window !== "undefined" &&
      (window.location.hostname.includes("localhost") ||
        window.location.hostname === "127.0.0.1");
    return isLocal ? `http://${subdomain}.localhost:8000${endpoint}` : endpoint;
  };

  const fetchInventory = useCallback(async () => {
    if (!branchInfo?.id) return;
    setLoading(true);
    try {
      const storageKey = `branch_auth_${subdomain}_${branchInfo.slug}`;
      const authData = JSON.parse(localStorage.getItem(storageKey) || "{}");
      const token = authData.token || "";

      const res = await fetch(
        getApiUrl(`/api/v1/inventory/?branch_id=${branchInfo.id}`),
        {
          headers: {
            Accept: "application/json",
            "X-Branch-ID": String(branchInfo.id),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (res.ok) {
        const data = await res.json();
        setInventories(Array.isArray(data) ? data : data.results || []);
      }
    } catch (err) {
      console.error("Inventory fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [branchInfo?.id, branchInfo?.slug, subdomain]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const handleStockAdjust = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem || adjustmentQty <= 0) return;

    setActionLoading(true);
    try {
      const storageKey = `branch_auth_${subdomain}_${branchInfo.slug}`;
      const authData = JSON.parse(localStorage.getItem(storageKey) || "{}");
      const token = authData.token || "";

      const res = await fetch(
        getApiUrl(`/api/v1/inventory/${selectedItem.id}/adjust-stock/`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Branch-ID": String(branchInfo.id),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            quantity: Number(adjustmentQty),
            adjustment_type: adjustmentType,
            notes: `Adjusted from ${branchInfo.name} Outlet Admin`,
          }),
        }
      );

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Stock adjustment failed");
      }

      Swal.fire({
        position: "top",
        icon: "success",
        title: "Stock Updated!",
        text: `Stock successfully ${adjustmentType === "add" ? "increased" : "decreased"}.`,
        timer: 1500,
        showConfirmButton: false,
        background: "#090d16",
        color: "#ffffff",
      });

      setSelectedItem(null);
      setAdjustmentQty(1);
      fetchInventory();
    } catch (err: any) {
      Swal.fire({
        position: "top",
        icon: "error",
        title: "Error",
        text: err.message || "Failed to update stock",
        background: "#090d16",
        color: "#ffffff",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const filteredItems = inventories.filter((item) => {
    const matchSearch =
      item.product_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.barcode?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchFilter =
      filterType === "all" ||
      (filterType === "low_stock" && item.is_low_stock);

    return matchSearch && matchFilter;
  });

  const totalStock = inventories.reduce(
    (acc, curr) => acc + (curr.stock_quantity || 0),
    0
  );
  const lowStockCount = inventories.filter((i) => i.is_low_stock).length;

  return (
    <div className="space-y-6 animate-fadeIn text-slate-100">
      {/* 1. Header & Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-[#0b101b] border border-slate-800/80 rounded-3xl p-5 flex items-center justify-between">
          <div>
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block">
              Total Tracked Products
            </span>
            <span className="text-2xl font-black font-mono text-white mt-1 block">
              {inventories.length}
            </span>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
            <Package className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-[#0b101b] border border-slate-800/80 rounded-3xl p-5 flex items-center justify-between">
          <div>
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block">
              Total Units In Stock
            </span>
            <span className="text-2xl font-black font-mono text-emerald-400 mt-1 block">
              {totalStock.toLocaleString()}
            </span>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-blue-500/10 text-blue-400 flex items-center justify-center">
            <Warehouse className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-[#0b101b] border border-slate-800/80 rounded-3xl p-5 flex items-center justify-between">
          <div>
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block">
              Low Stock Warnings
            </span>
            <span className="text-2xl font-black font-mono text-rose-400 mt-1 block">
              {lowStockCount}
            </span>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-rose-500/10 text-rose-400 flex items-center justify-center">
            <TrendingDown className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* 2. Controls & Search Bar */}
      <div className="bg-[#0b101b] border border-slate-800/80 rounded-3xl p-4 sm:p-5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by Product Name, SKU, or Barcode..."
            className="w-full pl-10 pr-4 py-2.5 rounded-2xl text-xs font-medium bg-[#0f172a] border border-slate-800 text-white outline-none focus:border-emerald-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setFilterType("all")}
            className={`px-3 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              filterType === "all"
                ? "bg-emerald-600 text-white"
                : "bg-slate-900 text-slate-400 hover:text-white border border-slate-800"
            }`}
          >
            All Inventory
          </button>
          <button
            onClick={() => setFilterType("low_stock")}
            className={`px-3 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
              filterType === "low_stock"
                ? "bg-rose-600 text-white"
                : "bg-slate-900 text-slate-400 hover:text-white border border-slate-800"
            }`}
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>Low Stock ({lowStockCount})</span>
          </button>
          <button
            onClick={fetchInventory}
            className="p-2.5 rounded-xl border border-slate-800 bg-[#0f172a] hover:bg-slate-800 text-slate-400 hover:text-white cursor-pointer transition-all"
            title="Refresh Inventory"
          >
            <RefreshCw
              className={`w-4 h-4 ${loading ? "animate-spin text-emerald-400" : ""}`}
            />
          </button>
        </div>
      </div>

      {/* 3. Inventory Table */}
      <div className="bg-[#0b101b] border border-slate-800/80 rounded-3xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="bg-[#0f172a] text-slate-400 font-bold uppercase text-[10px] tracking-wider border-b border-slate-800">
              <tr>
                <th className="py-4 px-5">Product Details</th>
                <th className="py-4 px-5">SKU / Barcode</th>
                <th className="py-4 px-5">Warehouse Bin</th>
                <th className="py-4 px-5 text-center">In Stock</th>
                <th className="py-4 px-5 text-center">Available</th>
                <th className="py-4 px-5 text-center">Status</th>
                <th className="py-4 px-5 text-right">Quick Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 font-medium">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-slate-500">
                    <RefreshCw className="w-6 h-6 animate-spin text-emerald-500 mx-auto mb-2" />
                    <span>Loading outlet warehouse items...</span>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-slate-500">
                    <Package className="w-10 h-10 mx-auto mb-2 text-slate-600" />
                    <p className="font-bold">No inventory records found</p>
                    <p className="text-[11px] mt-0.5">
                      Add products in this outlet to track warehouse stock.
                    </p>
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => (
                  <tr
                    key={item.id}
                    className="hover:bg-slate-900/50 transition-colors"
                  >
                    <td className="py-3.5 px-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#0f172a] border border-slate-800 overflow-hidden shrink-0 flex items-center justify-center">
                          {item.product_image ? (
                            <img
                              src={item.product_image}
                              alt={item.product_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Package className="w-5 h-5 text-slate-600" />
                          )}
                        </div>
                        <span className="font-bold text-white text-xs truncate max-w-[200px]">
                          {item.product_name}
                        </span>
                      </div>
                    </td>

                    <td className="py-3.5 px-5 font-mono text-[11px]">
                      <div className="flex flex-col">
                        <span className="text-slate-300">{item.sku || "N/A"}</span>
                        {item.barcode && (
                          <span className="text-slate-500 text-[10px] flex items-center gap-1">
                            <Barcode className="w-3 h-3" />
                            {item.barcode}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="py-3.5 px-5 text-slate-400">
                      <span>{item.warehouse_location || "Outlet Store"}</span>
                      {item.shelf_location && (
                        <span className="text-[10px] text-slate-500 block">
                          Shelf: {item.shelf_location}
                        </span>
                      )}
                    </td>

                    <td className="py-3.5 px-5 text-center font-mono font-bold text-sm text-white">
                      {item.stock_quantity}
                    </td>

                    <td className="py-3.5 px-5 text-center font-mono font-bold text-sm text-emerald-400">
                      {item.available_quantity}
                    </td>

                    <td className="py-3.5 px-5 text-center">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          item.is_low_stock
                            ? "bg-rose-500/10 text-rose-400 border border-rose-500/20"
                            : "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                        }`}
                      >
                        {item.is_low_stock ? "Low Stock" : "Healthy"}
                      </span>
                    </td>

                    <td className="py-3.5 px-5 text-right">
                      <button
                        onClick={() => setSelectedItem(item)}
                        className="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-700 hover:border-emerald-500 text-slate-200 hover:text-white text-xs font-bold transition-all cursor-pointer inline-flex items-center gap-1"
                      >
                        <ArrowUpDown className="w-3.5 h-3.5 text-emerald-400" />
                        <span>Adjust Stock</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 4. Stock Adjustment Modal */}
      {selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs animate-fadeIn">
          <div className="w-full max-w-md rounded-3xl bg-[#0b101b] border border-slate-800 text-white p-6 shadow-2xl space-y-4">
            <div className="border-b border-slate-800 pb-3">
              <span className="text-[10px] font-black uppercase tracking-wider text-emerald-400 block">
                Warehouse Stock Adjustment
              </span>
              <h3 className="text-base font-black text-white truncate mt-0.5">
                {selectedItem.product_name}
              </h3>
              <p className="text-xs text-slate-400 font-mono mt-0.5">
                Current Stock: {selectedItem.stock_quantity} units
              </p>
            </div>

            <form onSubmit={handleStockAdjust} className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setAdjustmentType("add")}
                  className={`py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 border transition-all cursor-pointer ${
                    adjustmentType === "add"
                      ? "bg-emerald-600 border-emerald-500 text-white"
                      : "bg-[#0f172a] border-slate-800 text-slate-400"
                  }`}
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Restock (+)</span>
                </button>

                <button
                  type="button"
                  onClick={() => setAdjustmentType("remove")}
                  className={`py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 border transition-all cursor-pointer ${
                    adjustmentType === "remove"
                      ? "bg-rose-600 border-rose-500 text-white"
                      : "bg-[#0f172a] border-slate-800 text-slate-400"
                  }`}
                >
                  <Minus className="w-3.5 h-3.5" />
                  <span>Deduct (-)</span>
                </button>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 mb-1.5">
                  Quantity
                </label>
                <input
                  type="number"
                  min="1"
                  required
                  value={adjustmentQty}
                  onChange={(e) => setAdjustmentQty(Number(e.target.value))}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-[#0f172a] border border-slate-800 text-white text-xs font-mono font-bold outline-none focus:border-emerald-500"
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setSelectedItem(null)}
                  className="flex-1 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-400 text-xs font-bold cursor-pointer"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold shadow-md cursor-pointer disabled:opacity-50"
                >
                  {actionLoading ? "Updating..." : "Confirm Update"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}